import React, { useEffect, lazy, useContext, useMemo } from 'react';
import { Switch, Route, Redirect, useParams, useHistory } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@material-ui/core/styles';
import { CssBaseline } from '@material-ui/core';
import { AlertDialog } from './components/AlertDialog/AlertDialog';
import { useTranslation } from 'react-i18next';
import { AppContext } from './AppContext';
import { tokenValidation, initAuth, tokenParse } from './helpers/validation/tokenValidation';
import { postRefreshToken } from './fetch/postRefreshToken';
import { getMyUser } from './fetch/users';
import { CookieConsent } from './components/CookieConsent/CookieConsent';
import ReactGA from "react-ga4";
const FrontPage = lazy(() => import('./FrontPage'));
const PrepareViewer = lazy(() => import('./PrepareViewer'));

const App = React.memo((props) => {
    const params = useParams();
    const history = useHistory();
    const ctx = useContext(AppContext);
    const { i18n } = useTranslation();

    const colors = useMemo(() => ({
        red: '#E3000F',
        yellow: '#ffff00',
        black: '#000000',
        white: '#FFFFFF',
        lightBlue: '#41b6e6',
        blue: '#3f51b5',
        lightGray: '#D3D3D3',
        blackOpacity04: 'rgba(0, 0, 0, 0.04)',
        blackOpacity12: 'rgba(0, 0, 0, 0.12)',
        blackOpacity38: 'rgba(0, 0, 0, 0.38)',
        blackOpacity42: 'rgba(0, 0, 0, 0.42)',
        blackOpacity54: 'rgba(0, 0, 0, 0.54)',
        blackOpacity87: 'rgba(0, 0, 0, 0.87)'
    })
    , []);

    const theme = useMemo(
        () => createTheme({
            overrides: {
                MuiCssBaseline: {
                    '@global': {
                        html: {
                            fontSize: ctx.fontSize
                        }
                    }
                }, 
                MuiDivider: {
                    root: {
                        backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.white,
                    }
                },
                MuiCheckbox: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54
                    },
                    colorSecondary: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                        '&.Mui-checked': {
                            color: ctx.isHighContrast === 1 ? colors.yellow  : '#f50057',
                        }
                    }
                },
                MuiTab: {
                    textColorPrimary: {
                        '&.Mui-disabled': {
                            color: ctx.isHighContrast === 1 ? 'rgba(255,255,255, 0.54)' : 'rgba(0, 0, 0,, 0.54)',
                        }
                    }
                },
                MuiIconButton: {
                    colorSecondary: {
                        color: ctx.isHighContrast === 1 ? colors.yellow : '#f50057',
                    },
                    edgeEnd: {
                        color: ctx.isHighContrast === 1 ? colors.yellow : '#f50057',
                    }
                },
                MuiButton: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.yellow : colors.blackOpacity12,
                            color: colors.black,
                            '&:hover': {
                                backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                                color: colors.black
                            },
                            '&:focus': {
                                backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                                color: colors.black
                            }
                        },
                        '&:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                            color: colors.black
                        },
                        '&:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                            color: colors.black
                        },
                        '&.Mui-disabled': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.black+'!important' : colors.white,
                            color: ctx.isHighContrast === 1 ? 'rgba(255,255,255, 0.54)' : 'rgba(0, 0, 0, 0.26)',
                        }
                    },
                    contained: {
                        color: ctx.isHighContrast === 1 ? colors.yellow : colors.black,
                        backgroundColor: ctx.isHighContrast === 1 ? colors.black : '#e0e0e0',
                        border: ctx.isHighContrast === 1 ? '2px solid '+colors.yellow : 'none',
                        '&.Mui-disabled  ': {
                            color: ctx.isHighContrast === 1 ? 'rgba(255,255,255, 0.54)' : 'rgba(0, 0, 0, 0.54)',
                        }
                    },
                },            
                MuiToggleButton: {
                    root: {
                        borderColor: ctx.isHighContrast === 1 ? colors.yellow : colors.blackOpacity12,
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity38,
                        '&.Mui-selected': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.yellow : colors.blackOpacity12,
                            color: colors.black,
                            '&:hover': {
                                backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                                color: colors.black
                            },
                            '&:focus': {
                                backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                                color: colors.black
                            }
                        },
                        '&:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                            color: colors.black
                        },
                        '&:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white+'!important' : colors.blackOpacity04,
                            color: colors.black
                        }
                    }
                },
                MuiSwitch: {
                    track: {
                        backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.black,
                        opacity: ctx.isHighContrast === 1 ? '0.54' : '0.38'
                    }
                },
                MuiAccordion: {
                    root: {
                        backgroundColor: ctx.isHighContrast === 1 ? colors.black+'!important' : colors.white,
                        color: ctx.isHighContrast === 1 ? colors.white : colors.black,
                    }
                },
                MuiMenu: {
                    list: {
                        backgroundColor: colors.white
                    }
                },
                MuiListItem: {
                    root: {
                        '&.Mui-selected': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.yellow : colors.blackOpacity12,
                            color: ctx.isHighContrast === 1 ? colors.black : colors.black
                        },
                        '&.Mui-selected:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.white : colors.black
                        },
                        '&.Mui-selected:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.white : colors.black
                        },
                        '&.Mui-focusVisible': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.white : colors.black
                        },
                    },
                    button: {
                        '&:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.white : colors.black
                        },
                        '&:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.white : colors.black
                        }
                    }
                },
                MuiListItemIcon: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                    }
                },
                MuiListSubheader: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                    }
                },
                MuiSvgIcon: {
                    root: {
                        '&.MuiSelect-icon': {
                            color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                            '&:hover': {
                                backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                                color: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity54,
                            },
                            '&:focus': {
                                backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                                color: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity54,
                            }
                        }
                    }
                },
                MuiAutocomplete: {
                    clearIndicatorDirty: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                        '&:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity54,
                        },
                        '&:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity54,
                        }
                    },
                    popupIndicator: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                        '&:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity54,
                        },
                        '&:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity54,
                        }
                    },
                    option: {
                        '&:hover': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: colors.black
                        },
                        '&:focus': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: colors.black
                        },
                        '&[data-focus="true"]': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                            color: colors.black
                        },
                        '&[aria-selected="true"]': {
                            backgroundColor: ctx.isHighContrast === 1 ? colors.yellow : 'rgba(0, 0, 0, 0.08)',
                            color: ctx.isHighContrast === 1 ? colors.black : colors.black
                        }
                    }
                },
                MuiFormHelperText: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                    }
                },
                MuiChip: {
                    root: {
                        backgroundColor: ctx.isHighContrast === 1 ? colors.white : '#e0e0e0',
                        color: colors.black,
                    }
                },
                MuiFormLabel: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.black,
                        '&.Mui-disabled': {
                            color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity38,
                        }
                    }
                },
                MuiInputBase: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.black,
                        '&.Mui-disabled': {
                            color: ctx.isHighContrast === 1 ? colors.blackOpacity87 : colors.blackOpacity38,
                            backgroundColor: colors.white
                        }
                    }
                },
                MuiInputLabel: {
                    root: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                    }
                },
                MuiInput: {
                    underline: {
                        '&:before': {
                            borderBottomColor: ctx.isHighContrast === 1 ? colors.lightGray : colors.blackOpacity42,
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottomColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity87,
                        },
                        '&:focus:not(.Mui-disabled):before': {
                            borderBottomColor: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity87,
                        }
                    }
                },
                MuiDialog: {
                    paper: {
                        border: ctx.isHighContrast === 1 ? '2px solid '+colors.yellow : 'none',
                    }
                },
                MuiTypography: {
                    colorTextSecondary: {
                        color: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                    }
                },
                MuiPaper:{
                    root: {
                        backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.white,
                        color: ctx.isHighContrast === 1 ? colors.white : colors.black,
                    }
                },
                MuiTouchRipple: {
                    rippleVisible: {
                        opacity: ctx.isHighContrast === 1 ? '0' : '0.2',
                    }
                }
            },           
            custom: {
                paper:{
                    backgroundColor: ctx.isHighContrast === 1 ? colors.black : colors.white,
                    color: ctx.isHighContrast === 1 ? colors.white : colors.black
                },
                backgroundColorPrimary: ctx.isHighContrast === 1 ? colors.black : colors.white,
                backgroundColorSecondary: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity04,
                backgroundColor3: ctx.isHighContrast === 1 ? colors.white : 'rgba(15, 15, 15, 0.68)',
                backgroundColor4: ctx.isHighContrast === 1 ? colors.black : 'rgba(15, 15, 15, 0.54)',
                backgroundColor5: ctx.isHighContrast === 1 ? colors.black : colors.blackOpacity04,
                backgroundColor6: ctx.isHighContrast === 1 ? colors.yellow : colors.blackOpacity12,
                textColorPrimary: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity54,
                colorPrimary: ctx.isHighContrast === 1 ? colors.white : colors.black,
                colorSecondary: ctx.isHighContrast === 1 ? colors.black : colors.white,
                color3: ctx.isHighContrast === 1 ? colors.yellow : colors.black,
                color4: ctx.isHighContrast === 1 ? colors.yellow : colors.white,
                color5: ctx.isHighContrast === 1 ? colors.white : colors.blackOpacity38,
                colorBlack: colors.black,
                colorBlackPink: ctx.isHighContrast === 1 ? colors.black : '#f50057',
                colorBlackPinkHover: ctx.isHighContrast === 1 ? colors.black : '#d1064e',
                colorBlackLightGray: ctx.isHighContrast === 1 ? colors.black : colors.lightGray,
                colorWhite: colors.white,
                colorYellow: colors.yellow,
                edit: ctx.isHighContrast === 1 ? colors.yellow :'#ff7700',
                add: ctx.isHighContrast === 1 ? colors.yellow :'#0000ff',
                show: ctx.isHighContrast === 1 ? colors.lightBlue :'#0000ff',
                colorPrimaryDisabled: ctx.isHighContrast === 1 ? 'rgba(255,255,255, 0.54)' : 'rgba(0, 0, 0,, 0.54)',
                borderColor: ctx.isHighContrast === 1 ? colors.yellow : colors.white,
                borderContrast: ctx.isHighContrast === 1 ? '2px solid '+colors.white : 'none',
                borderContrast2: ctx.isHighContrast === 1 ? '2px solid '+colors.white : '1px solid rgba(0, 0, 0, 0.23)',
                borderContrastYellow: ctx.isHighContrast === 1 ? '2px solid '+colors.yellow : 'none',
                borderContrastYellow2: ctx.isHighContrast === 1 ? '2px solid '+colors.yellow : '1px solid rgba(0, 0, 0, 0.23)',
                borderContrastRed: ctx.isHighContrast === 1 ? '2px solid #ff0000' : 'none',
                borderContrastWhite: ctx.isHighContrast === 1 ? '2px solid #ffffff' : 'none',
                header: ctx.isHighContrast === 1 ? colors.yellow : 'linear-gradient(90deg, rgba(15,15,15,1) 0%, rgba(15,15,15,0.75) 100%)',
                footer: ctx.isHighContrast === 1 ? colors.black : 'linear-gradient(90deg, rgba(15,15,15,1) 0%, rgba(15,15,15,0.75) 100%)',
                divider: ctx.isHighContrast === 1 ? colors.white : 'rgba(15, 15, 15, 0.68)',
                picker: ctx.isHighContrast === 1 ? '3px solid white' : '3px solid black',
                iconWSGI:ctx.isHighContrast === 1 ? colors.yellow : 'rgb(235, 0, 0)',
            },
            palette: {
                primary: {
                    main: ctx.isHighContrast === 1 ? colors.yellow : colors.blue,
                }
            }
        }),
        [ctx.fontSize, ctx.isHighContrast, colors]
    );

    useEffect(() => {
        if(!process.env.REACT_APP_DEFAULT_LANG_LIST.split(',').includes(params.lang)) return;

        ctx.setLanguage(params.lang);
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.lang])

    useEffect(() => {
        i18n.changeLanguage(ctx.language)
        document.documentElement.lang = ctx.language
        const pathname = history.location.pathname;
        const arrPathname = pathname.split('/');
        arrPathname[1] = ctx.language
        window.history.pushState({}, null, arrPathname.join('/'));
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.language])

    useEffect(() => {
        if(!process.env.REACT_APP_GA_ID)
            return;

        ReactGA.gtag('consent', 'default', {
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
            analytics_storage: ctx.cookieConsent?.analytics ? 'granted' : 'denied',
            anonymize_ip: !(ctx.cookieConsent?.analytics)
        });
        ReactGA.initialize(process.env.REACT_APP_GA_ID, {
            gtagOptions: {
                send_page_view: false
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(!process.env.REACT_APP_GA_ID || !window.gtag)
            return;

        ReactGA.gtag('consent', 'update', {
            analytics_storage: ctx.cookieConsent?.analytics ? 'granted' : 'denied',
            anonymize_ip: !(ctx.cookieConsent?.analytics)
        });

        if(!ctx.cookieConsent?.analytics){
            const gaCookies = ['_ga', '_gid', '_gat'];
            const gaPrefix = '_ga_'; // GA4 cookies start with _ga_

            const fullDomain = window.location.hostname;
            const partsDomain = fullDomain.split('.');
            const rootDomain = partsDomain.length > 2 ? partsDomain.slice(-2).join('.') : fullDomain;
            document.cookie.split(';').forEach(cookie => {
                const name = cookie.split('=')[0].trim();
                if (gaCookies.includes(name) || name.startsWith(gaPrefix)) {
                    // Remove cookie for root domain
                    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${rootDomain}`;
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.cookieConsent?.analytics]);

    useEffect(() => {
        if(ctx.isAuthorize?.state){
            localStorage.setItem("auth_state", JSON.stringify(ctx.isAuthorize));
        }else{
            localStorage.removeItem("auth_state");
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.isAuthorize])

    const refreshTokenCallback = (refreshToken, setIsAuthorize) => {
        postRefreshToken({refreshToken})
            .then(res => {
                const payload = tokenParse(res.data.token);
                setIsAuthorize({
                    state: true,
                    user: payload.username,
                    roles: payload.roles,
                    projects: payload.projects,
                    token: res.data.token,
                    refreshToken: res.data.refresh_token
                });
            })
            .catch(e => {
                setIsAuthorize(initAuth);
            })
    }

    useEffect(() => {
        if(!ctx.isAuthorize?.refreshToken){
            ctx.setIsAuthorize(initAuth);
            return;
        }

        if(!ctx.isAuthorize.state || !tokenValidation(ctx.isAuthorize.token)){
            refreshTokenCallback(ctx.isAuthorize.refreshToken, ctx.setIsAuthorize);
            return;
        }

        const refreshAfter =  Math.floor(tokenValidation(ctx.isAuthorize.token) * 2 / 3) * 1000;
        const refreshTimeout = setTimeout(() => {
            refreshTokenCallback(ctx.isAuthorize.refreshToken, ctx.setIsAuthorize);
        }, refreshAfter);

        return () => {
            clearTimeout(refreshTimeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx.isAuthorize])

    useEffect(() => {
        if(!ctx.isAuthorize.state || !tokenValidation(ctx.isAuthorize.token)) return;

        getMyUser(ctx.isAuthorize.token)
            .catch(err => {
                ctx.setIsAuthorize(initAuth);
                console.log(err);
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    
    return(
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AlertDialog />
            <CookieConsent />
            <Switch>
                <Route
                    path={[
                        `/:lang(${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',').join('|')})`,
                        `/:lang(${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',').join('|')})/(profile|admin|upload)`,
                        `/:lang(${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',').join('|')})/(profile|admin|upload)/(projects|users|groups|logos|csatsurveys)`,
                        `/:lang(${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',').join('|')})/(profile|admin|upload)/(projects|users|groups|logos|csatsurveys)/:id`
                    ]}
                    exact
                    render={() => <FrontPage />}
                />
                <Route
                    path='/:lang'
                    exact
                    render={() =>
                        <Redirect 
                            to={`/${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',')[0]}/${params.lang}`} 
                        />
                    }
                />
                <Route
                    path={`/:lang(${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',').join('|')})/:name?/:layer?/:lng?/:lat?/:zoom?/:height?/:yaw?/:pitch?/:roll?/:splitType?/:splitLayer?`}
                    render={() => <PrepareViewer />}
                />
                <Route 
                    render={() =>
                        <Redirect 
                            to={`/${process.env.REACT_APP_DEFAULT_LANG_LIST.split(',')[0]}`} 
                        />
                    }
                />
            </Switch>
        </ThemeProvider>
    )
})

export default App;